import { useMemo } from 'react'
import type { TemplateField } from 'shared/api/templateService'
import type { ParsedFieldConfig } from 'modules/permit/components/permit-request/utils/templateFieldUtils'
import { createTemplateFieldRenderer } from 'modules/permit/components/permit-request/fields/TemplateFieldRenderer'
import type { TemplateFieldDatasourceItem } from 'shared/api/templateService'

interface FieldDependencyInfo {
  params: Record<string, string>
  dependencyMissing: boolean
  config?: ParsedFieldConfig | null
}

interface UseTemplateFieldRendererParams {
  getFieldOptions: (fieldId: string) => TemplateFieldDatasourceItem[]
  getTemplateFieldDisplayValue: (field: TemplateField) => string
  getStoredTemplateFieldLabel: (field: TemplateField) => string | undefined
  getFieldValueByCode: (fieldCode: string) => string | undefined
  isFieldLoading: (fieldId: string) => boolean
  setTemplateFieldValue: (field: TemplateField, value: string | boolean | null) => void
  setTemplateFieldLabel: (field: TemplateField, label: string | null) => void
  tenantId: string
  permitNumber?: string | null
  parseFieldConfig: (field: TemplateField) => ParsedFieldConfig | null
  searchSelectLabels: Record<string, string>
  setSearchSelectLabels: React.Dispatch<React.SetStateAction<Record<string, string>>>
  isReadOnly: boolean
  canApproveCurrentStage: boolean
  getFieldDependencyInfo: (field: TemplateField) => FieldDependencyInfo
}

/**
 * Provides a memoised template field renderer so PermitRequestForm can stay focused
 * on composing sections instead of managing rendering dependencies directly.
 */
export const useTemplateFieldRenderer = ({
  getFieldOptions,
  getTemplateFieldDisplayValue,
  getStoredTemplateFieldLabel,
  getFieldValueByCode,
  isFieldLoading,
  setTemplateFieldValue,
  setTemplateFieldLabel,
  tenantId,
  permitNumber,
  parseFieldConfig,
  searchSelectLabels,
  setSearchSelectLabels,
  isReadOnly,
  canApproveCurrentStage,
  getFieldDependencyInfo,
}: UseTemplateFieldRendererParams) => {
  return useMemo(
    () =>
      createTemplateFieldRenderer({
        getFieldOptions,
        getTemplateFieldDisplayValue,
        getStoredTemplateFieldLabel,
        getFieldValueByCode,
        isFieldLoading,
        setTemplateFieldValue,
        setTemplateFieldLabel,
        tenantId,
        permitNumber,
        parseFieldConfig,
        searchSelectLabels,
        setSearchSelectLabels,
        isReadOnly,
        canApproveCurrentStage,
        getFieldDependencyInfo,
      }),
    [
      canApproveCurrentStage,
      getFieldDependencyInfo,
      getFieldOptions,
      getStoredTemplateFieldLabel,
      getTemplateFieldDisplayValue,
      getFieldValueByCode,
      isFieldLoading,
      isReadOnly,
      parseFieldConfig,
      permitNumber,
      searchSelectLabels,
      setSearchSelectLabels,
      setTemplateFieldValue,
      setTemplateFieldLabel,
      tenantId,
    ],
  )
}
